import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Home as HomeIcon,
  Chat as ChatIcon,
  SmartToy as AgentIcon,
  TableChart as TableIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getNavigationConfig, hasRoleAccess, UserRole, NavigationItem } from '../../config/roleConfig';

/**
 * Navigation organism component
 * Responsive navigation bar with authentication-aware menu items
 */
export const Navigation: React.FC = () => {
  const { user, isAuthenticated, logout, getUserRole, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Icon mapping for navigation items
  const iconMap: Record<string, any> = {
    '/': HomeIcon,
    '/admin-dashboard': AdminIcon, 
    '/hr-dashboard': DashboardIcon,
    '/sme-dashboard': DashboardIcon,
    '/interview-dashboard': DashboardIcon,
    '/agent': AgentIcon,
    '/account': AccountCircle,
    '/resumes': TableIcon,
    '/excel-comparison': CompareIcon,
    '/admin': AdminIcon, 
  };

  // Get navigation configuration and add icons
  const baseNavigationItems = getNavigationConfig();
  const navigationItems: NavigationItem[] = baseNavigationItems.map(item => ({
    ...item,
    icon: iconMap[item.path] || HomeIcon,
  }));

  // Get current user role
  const currentUserRole = getUserRole() as UserRole;

  // Filter navigation items based on authentication and role access
  const visibleItems = navigationItems.filter(item => {
    // Show public items to everyone
    if (item.public) return true;
    
    // For private items, user must be authenticated
    if (!isAuthenticated) return false;
    
    // Check role-based access
    return hasRoleAccess(currentUserRole, item.allowedRoles);
  });

  // Add these debug logs AFTER visibleItems is defined
  console.log('=== DEBUG INFO ===');
  console.log('User object:', user);
  console.log('Is authenticated:', isAuthenticated);
  console.log('Current user role:', currentUserRole);
  console.log('Visible items:', visibleItems.map(item => ({ label: item.label, path: item.path })));
  console.log('==================');

  // Handle navigation
  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuAnchor(null);
  };

  // Handle user menu
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/');
  };

  // Mobile menu handlers
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  // Check if current path is active
  const isActivePath = (path: string) => location.pathname === path;

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        {/* Logo */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            cursor: 'pointer',
            mr: 4,
          }}
          onClick={() => navigate('/')}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 800,
              fontSize: '1.5rem',
              background: 'linear-gradient(135deg, #fff 0%, #e0e7ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
            }}
          >
            SmartHire
          </Typography>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
            }}
          >
            AI
          </Box>
        </Box>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
            {visibleItems.map(item => (
              <Button
                key={item.path}
                color="inherit"
                onClick={() => handleNavigate(item.path)}
                startIcon={<item.icon />}
                sx={{
                  borderRadius: 2,
                  backgroundColor: isActivePath(item.path)
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        )}

        {/* Mobile menu button */}
        {isMobile && (
          <Box sx={{ flexGrow: 1 }}>
            <IconButton color="inherit" onClick={handleMobileMenuOpen} edge="start">
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        {/* User section */}
        <Box sx={{ flexGrow: 0 }}>
          {isAuthenticated ? (
            <>
              <IconButton size="large" onClick={handleUserMenuOpen} color="inherit">
                <Avatar sx={{ width: 32, height: 32 }}>{user?.firstName?.[0]}</Avatar>
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    handleNavigate('/account');
                    handleUserMenuClose();
                  }}
                >
                  <AccountCircle sx={{ mr: 1 }} /> My Account
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1 }} /> Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              color="inherit"
              onClick={() => navigate('/login')}
              startIcon={<LoginIcon />}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Login
            </Button>
          )}
        </Box>

        {/* Mobile menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {visibleItems.map(item => (
            <MenuItem
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              selected={isActivePath(item.path)}
            >
              <item.icon sx={{ mr: 1 }} />
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
